import type { ChartConfiguration, ChartOptions } from 'chart.js/auto';
import { Chart as ChartJS } from 'chart.js/auto';
import dragdataPlugin from 'chartjs-plugin-dragdata';
import { useEffect, useState, useRef } from 'react';
import { useMetricType } from 'hooks/useMetricType';
import * as S from '../../PropertyPanel.styles';
import { Radio } from '@mui/material';
import theme from 'theme/theme';
import { SwitchWithLabel } from 'components/SwitchWithLabel';
import {
  CONVERSIONS,
  convertToCurrentMetric,
  convertToDefaultMetric,
} from 'util/conversions';

ChartJS.register(dragdataPlugin);

interface PSDCurveCardInterface {
  percent: number[];
  size: number[];
  updatePsd: (percent: number[], size: number[]) => void;
  setCurveEditIndex: (editIndex: number | undefined) => void;
}

interface PointInterface {
  x: number;
  y: number;
}

enum RADIO_MODE {
  LINE = 'line',
  BAR = 'bar',
}

ChartJS.defaults.font.family = 'Sandvik Sans Text';
ChartJS.defaults.font.size = 14;

export const PSDCurve = ({
  percent,
  size,
  updatePsd,
  setCurveEditIndex,
}: PSDCurveCardInterface) => {
  const metricType = useMetricType();

  const [showGridLines, setShowGridLines] = useState(true);
  const [psdData, setPsdData] = useState<PointInterface[] | null>(null);
  const [modifyIndex, setModifyIndex] = useState<number | undefined>(undefined);
  const [modifyValue, setModifyValue] = useState<PointInterface | undefined>(
    undefined,
  );
  const [radioMode, setRadioMode] = useState(RADIO_MODE.LINE);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<ChartJS | null>(null);

  useEffect(() => {
    if (percent && size) {
      const newPsdData = percent.map((psdValue, index) => {
        const sizeValue = size[index];
        const convertedSizeValue = convertToCurrentMetric({
          value: sizeValue,
          metricType,
          conversionType: CONVERSIONS.MILLIMETER,
        });
        return { y: psdValue, x: convertedSizeValue ?? 0 };
      });
      setPsdData(newPsdData);
    }
  }, [percent, size, metricType]);

  const [dragEnd, setDragEnd] = useState(false);
  useEffect(() => {
    if (dragEnd) {
      if (
        typeof modifyValue !== 'undefined' &&
        typeof modifyIndex !== 'undefined' &&
        psdData
      ) {
        const newPsdData = [...psdData];
        const prevPoint: PointInterface | undefined = psdData[modifyIndex - 1];
        const nextPoint: PointInterface | undefined = psdData[modifyIndex + 1];

        newPsdData[modifyIndex] = modifyValue;

        /* CHECK X INTERVAL */
        if (typeof nextPoint !== 'undefined' && nextPoint.x <= modifyValue.x) {
          newPsdData[modifyIndex].x =
            metricType.value === 'IMPERIAL'
              ? nextPoint.x - 0.001
              : nextPoint.x - 0.1;
        } else if (
          typeof prevPoint !== 'undefined' &&
          prevPoint.x >= modifyValue.x
        ) {
          newPsdData[modifyIndex].x =
            metricType.value === 'IMPERIAL'
              ? prevPoint.x + 0.001
              : prevPoint.x + 0.1;
        }

        /* CHECK Y INTERVAL */
        if (typeof nextPoint !== 'undefined' && nextPoint.y < modifyValue.y) {
          newPsdData[modifyIndex].y = nextPoint.y - 0.1;
        } else if (
          typeof prevPoint !== 'undefined' &&
          prevPoint.y >= modifyValue.y
        ) {
          newPsdData[modifyIndex].y = prevPoint.y + 0.1;
        }

        setModifyValue(undefined);
        setModifyIndex(undefined);
        setDragEnd(false);

        const newPercentList = newPsdData.map(
          (item) => Math.round(item.y * 100) / 100,
        );
        const convSizeList = newPsdData.map((item) =>
          convertToDefaultMetric({
            value: item.x,
            metricType,
            conversionType: CONVERSIONS.MILLIMETER,
          }),
        );
        const newSizeList = convSizeList.map((item) =>
          item !== undefined ? Math.round(item * 100) / 100 : 0,
        );
        updatePsd(newPercentList, newSizeList);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragEnd, modifyValue, modifyIndex, psdData, metricType]);

  useEffect(() => {
    setCurveEditIndex(modifyIndex);
  }, [modifyIndex, setCurveEditIndex]);

  useEffect(() => {
    const ctx = canvasRef.current;

    if (!ctx || !psdData || psdData.length === 0) return;

    const context = ctx.getContext('2d', { willReadFrequently: true });
    if (!context) return;

    const currentChart = chartInstanceRef.current;
    const chartModeIsSame =
      currentChart &&
      'type' in currentChart.config &&
      radioMode === currentChart.config.type;

    if (currentChart && chartModeIsSame) {
      // Update existing chart
      currentChart.data.datasets[0].data = psdData;
      if (currentChart.options.scales?.y?.grid) {
        currentChart.options.scales.y.grid.display = showGridLines;
      }
      if (currentChart.options.scales?.x?.grid) {
        currentChart.options.scales.x.grid.display = showGridLines;
      }
      currentChart.update('none');
    } else {
      // Destroy old chart if exists
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }

      const lineOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            grid: {
              display: showGridLines,
            },
            title: {
              display: true,
              text: 'Percent [%]',
            },
            min: 0,
          },
          x: {
            type: 'logarithmic',
            grid: {
              display: showGridLines,
            },
            title: {
              display: true,
              text: metricType.value === 'IMPERIAL' ? 'Size [in]' : 'Size [mm]',
            },
            min: psdData[0].x,
            ticks: {
              maxRotation: 0,
              autoSkipPadding: 20,
              callback: (value: number | string) => {
                return Number(value.toString());
              },
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          dragData: {
            dragX: true,
            showTooltip: true,
            onDragStart: (
              _e: MouseEvent | TouchEvent,
              _datasetIndex: number,
              index: number,
              _value: unknown,
            ) => {
              setModifyIndex(index);
            },
            onDrag: (
              e: MouseEvent | TouchEvent,
              _datasetIndex: number,
              _index: number,
              value: unknown,
            ) => {
              (e.target as HTMLElement).style.cursor = 'grabbing';
              const chart = chartInstanceRef.current;
              if (!chart) return;

              const pointValue = value as PointInterface;
              const xAxisMax: number = chart.scales.x.max;

              if (pointValue.x >= xAxisMax) {
                setModifyValue({
                  ...pointValue,
                  x:
                    metricType.value === 'IMPERIAL'
                      ? pointValue.x + 1
                      : pointValue.x + 20,
                });
              } else {
                setModifyValue(pointValue);
              }
            },
            onDragEnd: (
              e: MouseEvent | TouchEvent,
              _datasetIndex: number,
              _index: number,
            ) => {
              (e.target as HTMLElement).style.cursor = 'auto';
              setDragEnd(true);
            },
          },
        },
      };

      const barOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            grid: {
              display: showGridLines,
            },
            title: {
              display: true,
              text: 'Percent [%]',
            },
            min: 0,
          },
          x: {
            grid: {
              display: showGridLines,
            },
            title: {
              display: true,
              text: 'Size [mm]',
            },
            min: 0,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
        },
      };

      const chartConfig: ChartConfiguration = {
        type: radioMode,
        data: {
          labels:
            radioMode === RADIO_MODE.BAR
              ? psdData.map((point: PointInterface) => point.x)
              : undefined,
          datasets: [
            {
              data: psdData,
              borderColor: theme.palette.info.main,
              backgroundColor: theme.graph.colors.primaryFill,
              cubicInterpolationMode: 'default',
              fill: true,
              tension: 0.2,
            },
          ],
        },
        options: radioMode === RADIO_MODE.LINE ? lineOptions : barOptions,
      };

      chartInstanceRef.current = new ChartJS(ctx, chartConfig);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [psdData, radioMode, showGridLines, metricType]);

  // 'Simulate curve' should not be visible for now
  const showRadio = false;

  return (
    <>
      <div
        style={{
          position: 'relative',
          height: `calc(100vh - 140px)`,
          maxWidth: '100%',
          width: '1400px',
          flex: '0 0 auto',
        }}
      >
        <canvas ref={canvasRef} />
      </div>
      <S.CurveMenu>
        <S.CurveSwitchWrapper>
          <SwitchWithLabel
            value={showGridLines}
            setValue={(val) => setShowGridLines(val)}
            label={`Show grid`}
          />
        </S.CurveSwitchWrapper>
        {showRadio ? (
          <S.CurveRadioContainer>
            <S.CurveRadioWrapper>
              <Radio
                checked={radioMode === RADIO_MODE.LINE}
                onChange={() => setRadioMode(RADIO_MODE.LINE)}
                color='info'
              />
              <>{'Simulate curve'}</>
            </S.CurveRadioWrapper>
          </S.CurveRadioContainer>
        ) : null}
      </S.CurveMenu>
    </>
  );
};
